import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Prescription, PrescriptionDataTransformer, PrescriptionFormData } from "@/types/prescription";

export const usePrescriptionActions = (patientId: string, onDataChange: () => void) => {
  const { currentUser } = useAuth();

  // ✅ FIXED: Use refs for stable references to avoid stale closures
  const patientIdRef = useRef(patientId);
  const onDataChangeRef = useRef(onDataChange);
  const currentUserRef = useRef(currentUser);

  // Update refs when dependencies change
  useEffect(() => {
    patientIdRef.current = patientId;
    onDataChangeRef.current = onDataChange;
    currentUserRef.current = currentUser;
  }, [patientId, onDataChange, currentUser]);

  // Prescription management states
  const [showNewPrescription, setShowNewPrescription] = useState(false);
  const [viewingPrescription, setViewingPrescription] = useState<Prescription | null>(null);
  const [copyData, setCopyData] = useState<Partial<PrescriptionFormData> | null>(null);
  // Bumped on every Copy so an already-open pad remounts with the copied data (the form only reads initialData on mount)
  const [copyNonce, setCopyNonce] = useState(0);

  // ✅ FIXED: Cleanup function to reset all states
  const cleanup = useCallback(() => {
    setShowNewPrescription(false);
    setViewingPrescription(null);
    setCopyData(null);
  }, []);

  // Handle new prescription
  const handleNewPrescription = useCallback(() => {
    setCopyData(null);
    setShowNewPrescription(true);
  }, []);

  const handleCopyPrescription = useCallback((prescription: Prescription) => {
    const copyPrescriptionData = PrescriptionDataTransformer.toCopyData(
      prescription,
      patientIdRef.current,
      currentUserRef.current?.doctor_id || prescription.doctor_id || "",
      currentUserRef.current?.clinic_id || prescription.clinic_id || "",
    );
    setCopyData(copyPrescriptionData);
    setCopyNonce((n) => n + 1);
    setShowNewPrescription(true);
  }, []);

  // ✅ FIXED: Handle prescription saved with stable reference
  const handlePrescriptionSaved = useCallback(() => {
    onDataChangeRef.current(); // Use ref to avoid stale closure
    setShowNewPrescription(false);
    setCopyData(null);
  }, []); // ✅ FIXED: No dependencies needed since we use refs

  // ✅ FIXED: Handle prescription cancel with cleanup
  const handlePrescriptionCancel = useCallback(() => {
    setShowNewPrescription(false);
    setCopyData(null);
  }, []);

  // Handle view prescription
  const handleViewPrescription = useCallback((prescription: Prescription) => {
    setViewingPrescription(prescription);
  }, []);

  // Handle close view
  const handleCloseView = useCallback(() => {
    setViewingPrescription(null);
  }, []);

  // ✅ FIXED: Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      cleanup();
    };
  }, [cleanup]);

  return {
    // State
    showNewPrescription,
    viewingPrescription,
    copyData,
    copyNonce,

    // Actions
    handleNewPrescription,
    handleCopyPrescription,
    handlePrescriptionSaved,
    handlePrescriptionCancel,
    handleViewPrescription,
    handleCloseView,

    // Setters (for direct state manipulation if needed)
    setShowNewPrescription,
    setViewingPrescription,
    setCopyData,

    // ✅ FIXED: Add cleanup function for external use
    cleanup
  };
}; 